<script setup lang="ts">
import Close from '~/assets/icons/close/24/close-L-gray.svg?component'
import useMainStore from '~/shared/store'

const mainStore = useMainStore()

const props = defineProps<{
  modalIsOpen: boolean,
  modalTitle?: string,
  fullHeightOnMobile?: boolean,
  size?: 'big',
  isScrolling?: boolean
}>()

const emits = defineEmits(['close-modal'])
let startY = 0
let isSwiping = false

const enableTransition = ref(true)

watch(() => props.modalIsOpen, (modalIsOpen) => {
  if(modalIsOpen) {
    mainStore.setBodyFixed(true)
  }else {
    mainStore.setBodyFixed(false)
  }
})

watch(() => props.fullHeightOnMobile, (fullHeightOnMobile) => {
  enableTransition.value = false
  setTimeout(() => {
    enableTransition.value = true
  }, 0)
})

function closeModal() {
  mainStore.setBodyFixed(false)
  emits('close-modal', true)
}

function handleSwipe(event: TouchEvent) {
    if (!props.fullHeightOnMobile) {
      if (event.type === 'touchstart') {
          isSwiping = true
          startY = event.touches[0].clientY
      }
      if (event.type === 'touchmove' && isSwiping) {
      const endY = event.touches[0].clientY;
          if (endY - startY > 10) {
              isSwiping = false
              closeModal();
          }
      }
      if (event.type === 'touchend') {
        isSwiping = false
      }
    }
  }
</script>

<template>
  <div
    class="modal"
    :class="[
      (modalIsOpen && enableTransition) && 'active',
      isScrolling && 'scrolling'
    ]"
    @touchstart.passive="handleSwipe"
    @touchmove.passive="handleSwipe"
    @touchend="handleSwipe"
  >
    <div
      class="modal-content"
      :class="[
        fullHeightOnMobile && 'full-height',
        enableTransition && 'has-transition',
        size,
      ]"
    >
      <div
        v-if="modalTitle"
        class="modal-content__title"
        v-html="modalTitle"
      >
      </div>
      <div class="modal-content__wrapper">
        <slot />
      </div>
      <div
        @click="closeModal"
        class="modal-close__wrapper"
      >
        <Close
          class="modal-close"
        />
      </div>
    </div>
    <div
      @click="closeModal"
      class="modal-backdrop"
    >

    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(var(--vh, 1vh) * 100);
  z-index: 1000;
  visibility: hidden;

  &.scrolling {
    overflow: auto;
    padding: 40px 0;
    display: flex;
    justify-content: center;
    height: calc(var(--vh, 1vh) * 100);
    background: rgba(31, 31, 31, 0.50);
    transition: background-color .3s, opacity .3s, visibility .3s;

    @include sm-breakpoint {
      padding: 0;
      overflow: hidden;
    }

    .modal {
      &-content {
        position: relative !important;
        top: auto !important;
        left: auto !important;
        bottom: auto !important;
        right: auto !important;
        transform: none !important;
        z-index: 1;
        height: fit-content;
        margin: auto 0;
        @include sm-breakpoint {
          margin: auto 0 0 0;
          width: 100%;
          border-radius: 20px 20px 0 0;

          &.full-height {
            height: 100%;
            overflow: auto;
            border-radius: 0;
          }
        }
      }

      &-backdrop {
        top: 0;
        background-color: transparent;
        min-height: calc(100vh - 80px);
      }
    }
  }

  &.active {
    visibility: visible;
    .modal-content {
      @include mf-xs-breakpoint {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
      }

      @include xs-breakpoint {
        &.full-height {
          bottom: auto;
          right: 0;
          opacity: 1;
        }
        &:not(.full-height) {
          bottom: 0;
        }

        &.has-transition {
          transition: all .3s;
        }
      }
    }
    .modal-backdrop  {
      opacity: 1;
      visibility: visible;
    }
  }
  &-content {
    position: absolute;
    z-index: 1;
    width: 100%;
    padding: 32px;
    background: $bg_page;
    border-radius: 20px;
    &__title {
      @include font(24px, 28px, 600)
    }
    @include mf-xs-breakpoint {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) scale(0.9);
      opacity: 0;
      width: fit-content;
      min-width: 300px;
      //max-width: 600px;
      &.big {
        max-width: 856px;
        width: 100%;
      }
    }
    @include md-breakpoint {
      &.big {
        width: calc(100% - 32px);
      }
    }
    @include xs-breakpoint {
      padding: 32px 20px 28px;
      &__title {
        @include font(20px, 24px, 600);
      }
      &.big {
        width: 100%;
      }
      &.full-height {
        top: 0;
        bottom: auto;
        right: 0;
        border-radius: 0;
        max-height: 100%;
        height: 100%;
        overflow: auto;

        .modal-close {
          &__wrapper {
            width: 48px;
            height: 48px;
            background-color: transparent;
            border-radius: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            transform: none;
            left: auto;
            right: 0;
            top: 0;

            svg {
              display: block;
              :deep(path) {
                fill: $control-content_primary;
              }
            }
          }

        }
      }
      &:not(.full-height) {
        width: 100%;
        right: 0;
        bottom: -100%;
        border-radius: 20px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    &.has-transition {
      transition: opacity .3s;
    }
  }
  &-close {
    &__wrapper {
      position: absolute;
      right: 12px;
      top: 12px;
      cursor: pointer;
      @include xs-breakpoint {
        right: auto;
        left: 50%;
        transform: translateX(-50%);
        top: 8px;
        width: 40px;
        height: 4px;
        border-radius: 5px;
        background-color: $bg_border;
        svg {
          display: none;
        }
      }

      svg {
        @include sm-breakpoint {
          :deep(path) {
            fill: $control-content_primary;
          }
        }
        &:hover:not(:active) {
          @include mf-breakpoint {
            :deep(path) {
              fill: $control-content_primary;
            }
          }
        }

        @include xs-breakpoint {
          svg {
            :deep(path) {
              fill: $control-content_primary;
            }
          }
        }
      }

    }
  }
  &-backdrop {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    width: 100%;
    height: 100%;
    background: rgba(31, 31, 31, 0.50);
    transition: 0.3s all;
  }
}
</style>
